import type { ReactNode } from 'react'

export type PeriodRow = { period_no: number; label: string | null; start_time: string | null; end_time: string | null }

export type TeacherOpt = {
  teacher_user_id: number | null
  teacher_code: string
  display_name: string
}

export type ViewerEntry = {
  untis_lesson_id?: string | null
  weekday: number
  period_no: number
  subject_id?: number | null
  subject_code: string | null
  subject_name: string | null
  course_name: string | null
  class_name: string | null
  teacher_display_name: string | null
  teacher_code: string | null
  room_name: string | null
  parent_kind: string | null
}

export type ViewerMode = 'class' | 'teacher' | 'student'

export function parseApiError(error: unknown): string {
  const e = error as { response?: { data?: { error?: string; message?: string } }; message?: string }
  return e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Unbekannter Fehler'
}

export function teacherOptionValue(t: TeacherOpt): string {
  if (t.teacher_user_id != null && Number(t.teacher_user_id) > 0) return `u:${t.teacher_user_id}`
  return `c:${t.teacher_code || t.display_name}`
}

export function formatPeriodLabel(p: PeriodRow): string {
  const base = p.label || `${p.period_no}. Stunde`
  if (p.start_time && p.end_time) return `${base} (${p.start_time}–${p.end_time})`
  return base
}

function uniqNonEmpty(values: (string | null | undefined)[]): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const v of values) {
    const s = String(v || '').trim()
    if (!s || seen.has(s)) continue
    seen.add(s)
    out.push(s)
  }
  return out
}

/** Heller Hintergrund → dunkle Schrift (sRGB-Annäherung). */
function isLightSubjectHex(hex: string): boolean {
  const h = String(hex || '')
    .trim()
    .replace(/^#/, '')
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return true
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  const l = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
  return l > 0.55
}

/** Mehrere DB-Zeilen mit gleicher UNTIS-Lektions-ID = eine Großveranstaltung (Team, Räume, Klassen). */
function groupEntriesByUntisLesson(entries: ViewerEntry[]): ViewerEntry[][] {
  const groups = new Map<string, ViewerEntry[]>()
  const order: string[] = []
  entries.forEach((e, idx) => {
    const raw = e.untis_lesson_id != null ? String(e.untis_lesson_id).trim() : ''
    const key = raw ? `lid:${raw}` : `solo:${idx}`
    if (!groups.has(key)) {
      groups.set(key, [])
      order.push(key)
    }
    groups.get(key)!.push(e)
  })
  return order.map((k) => groups.get(k)!)
}

export function formatCell(
  entries: ViewerEntry[],
  mode: ViewerMode,
  colorBySubjectId: Map<number, string>
): ReactNode {
  if (!entries.length) {
    return <span className="text-gray-400 dark:text-gray-500">—</span>
  }
  const groups = groupEntriesByUntisLesson(entries)
  return (
    <div className="space-y-1.5">
      {groups.map((rows, gIdx) => {
        const lid = rows[0].untis_lesson_id != null ? String(rows[0].untis_lesson_id).trim() : ''
        const title =
          rows.find((r) => r.subject_name)?.subject_name ||
          rows.find((r) => r.subject_code)?.subject_code ||
          rows.find((r) => r.course_name)?.course_name ||
          '—'
        const teachers = uniqNonEmpty(rows.map((r) => r.teacher_display_name || r.teacher_code))
        const classes = uniqNonEmpty(rows.map((r) => r.class_name))
        const rooms = uniqNonEmpty(rows.map((r) => r.room_name))
        const rawSid = rows.map((r) => r.subject_id).find((id) => id != null && Number(id) > 0)
        const sid = rawSid != null ? Number(rawSid) : null
        const accentHex = sid != null && Number.isFinite(sid) ? colorBySubjectId.get(sid) : undefined
        const lightBg = accentHex ? isLightSubjectHex(accentHex) : true

        const shellClass = accentHex
          ? `rounded border p-1.5 text-left text-xs ${
              lightBg ? 'border-gray-900/15 shadow-sm' : 'border-white/20 shadow-sm'
            }`
          : 'rounded border border-gray-200 bg-gray-50/80 p-1.5 text-left text-xs dark:border-gray-600 dark:bg-gray-800/60'

        const titleClass = accentHex
          ? lightBg
            ? 'font-semibold text-gray-900'
            : 'font-semibold text-white'
          : 'font-semibold text-gray-900 dark:text-gray-100'

        const detailClass = accentHex
          ? lightBg
            ? 'text-gray-800'
            : 'text-white/90'
          : 'text-gray-600 dark:text-gray-400'

        const mutedClass = accentHex
          ? lightBg
            ? 'text-gray-600'
            : 'text-white/75'
          : 'text-gray-500 dark:text-gray-500'

        return (
          <div
            key={lid ? `lid-${lid}-${gIdx}` : `solo-${gIdx}-${rows[0].period_no}`}
            className={shellClass}
            style={accentHex ? { backgroundColor: accentHex } : undefined}
          >
            <div className={titleClass}>{title}</div>
            {mode === 'class' && teachers.length > 0 && (
              <div className={detailClass}>{teachers.join(' · ')}</div>
            )}
            {mode === 'teacher' && classes.length > 0 && (
              <div className={detailClass}>Klassen {classes.join(', ')}</div>
            )}
            {mode === 'student' && teachers.length > 0 && (
              <div className={detailClass}>{teachers.join(' · ')}</div>
            )}
            {rooms.length > 0 && (
              <div className={mutedClass}>Räume {rooms.join(', ')}</div>
            )}
          </div>
        )
      })}
    </div>
  )
}
